import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AgentBotsAPI from '../../api/agentBots';
import InboxesAPI from '../../api/inboxes';
import { throwErrorMessage } from '../utils/api';

// `bot_config` é substituído por inteiro no backend, então o que sai daqui é o
// objeto completo — quem chama a action é responsável por preservar as outras
// chaves. Ver AgentBotModal.vue.
const buildBotConfig = data => {
  const botConfig = data?.bot_config;
  return botConfig && typeof botConfig === 'object' ? botConfig : {};
};


export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isUpdatingAvatar: false,
    isFetchingAgentBot: false,
    isSettingAgentBot: false,
    isDisconnecting: false,
  },
  agentBotInbox: {},
};

export const getters = {
  getBots($state) {
    return $state.records;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getBot: $state => botId => {
    const [bot] = $state.records.filter(record => record.id === Number(botId));
    return bot || {};
  },
  getActiveAgentBot: $state => inboxId => {
    const associatedAgentBotId = $state.agentBotInbox[Number(inboxId)];
    return getters.getBot($state)(associatedAgentBotId);
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isFetching: true });
    try {
      const response = await AgentBotsAPI.get();
      commit(types.SET_AGENT_BOTS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, botData) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isCreating: true });
    try {
      // Create FormData for file upload
      // MESMA FORMA DO UPDATE: JSON para o payload, multipart só para o arquivo.
      // Serializar o `bot_config` em multipart perde tipo — `null` some, booleano
      // e número viram string, objeto aninhado vira "[object Object]" e array
      // vazio desaparece. JSON preserva os quatro.
      let response = await AgentBotsAPI.create({
        name: botData.name,
        description: botData.description,
        bot_type: botData.bot_type || 'webhook',
        outgoing_url: botData.outgoing_url,
        bot_config: buildBotConfig(botData),
      });

      // O avatar sobe depois, com o id que a criação devolveu. Se falhar, o robô
      // já existe e a credencial já pode ser mostrada — perder a imagem é
      // recuperável, perder o token não, porque ele só aparece uma vez.
      if (
        response.data?.id &&
        typeof File !== 'undefined' &&
        botData.avatar instanceof File
      ) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', botData.avatar);
        try {
          const comAvatar = await AgentBotsAPI.update(
            response.data.id,
            avatarFormData
          );
          // Só substitui se veio corpo. Uma resposta sem `data` sobrescreveria o
          // robô recém-criado por `undefined` e derrubaria o commit.
          if (comAvatar?.data) response = comAvatar;
        } catch (avatarError) {
          // Silencioso de propósito: o robô foi criado. O chamador decide o que
          // dizer sobre a imagem.
        }
      }

      commit(types.ADD_AGENT_BOT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isCreating: false });
    }
    return null;
  },

  update: async ({ commit }, { id, data }) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isUpdating: true });
    try {
      // FormData crua passa DIRETO. Sem esta saída, o corpo JSON montado abaixo
      // leria `data.name` e `data.bot_config` de uma FormData — onde eles não
      // existem como propriedades — e enviaria nome vazio e `bot_config: {}`,
      // apagando as permissões de quem só quis trocar o avatar.
      if (data instanceof FormData) {
        const response = await AgentBotsAPI.update(id, data);
        commit(types.EDIT_AGENT_BOT, response.data);
        return;
      }

      // O avatar exige multipart, mas multipart não sabe dizer "lista vazia":
      // `bot_config[permissions][]=` chega no Rails como [''] e o modelo o
      // recusa (422). E lista vazia é exatamente como se revoga tudo de um
      // robô. Por isso o arquivo vai numa requisição só dele e o resto vai em
      // JSON, que expressa [] sem ambiguidade.
      // CONFIGURAÇÃO PRIMEIRO, imagem depois. A ordem importa porque as duas
      // requisições são independentes: se o avatar falhar primeiro, nome,
      // descrição e permissões nunca chegam a ser salvos — e o usuário perde a
      // edição inteira por causa de uma imagem. Invertido, o que ele digitou
      // está gravado e só a imagem precisa ser reenviada.
      let response = await AgentBotsAPI.update(id, {
        name: data.name,
        description: data.description,
        bot_type: data.bot_type || 'webhook',
        outgoing_url: data.outgoing_url,
        bot_config: buildBotConfig(data),
      });

      // `instanceof File`, não truthy. `data.avatar` também carrega a URL do
      // avatar ATUAL quando nada foi trocado — e uma string truthy fazia a store
      // reenviar a URL como se fosse arquivo novo, a cada salvamento.
      if (typeof File !== 'undefined' && data.avatar instanceof File) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', data.avatar);
        response = await AgentBotsAPI.update(id, avatarFormData);
      }

      // UM commit só, com o estado final. Commitar depois de cada requisição
      // fazia a lista piscar com o robô sem avatar antes de recebê-lo.
      commit(types.EDIT_AGENT_BOT, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isDeleting: true });
    try {
      await AgentBotsAPI.delete(id);
      commit(types.DELETE_AGENT_BOT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isDeleting: false });
    }
  },

  deleteAgentBotAvatar: async ({ commit }, id) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isUpdatingAvatar: true });
    try {
      await AgentBotsAPI.deleteAgentBotAvatar(id);
      // Update the thumbnail to empty string after deletion
      commit(types.UPDATE_AGENT_BOT_AVATAR, { id, thumbnail: '' });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isUpdatingAvatar: false });
    }
  },

  show: async ({ commit }, id) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await AgentBotsAPI.show(id);
      commit(types.ADD_AGENT_BOT, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isFetchingItem: false });
    }
  },

  fetchAgentBotInbox: async ({ commit }, inboxId) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isFetchingAgentBot: true });
    try {
      const { data } = await InboxesAPI.getAgentBot(inboxId);
      const { agent_bot: agentBot = {} } = data || {};
      commit(types.SET_AGENT_BOT_INBOX, { agentBotId: agentBot.id, inboxId });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isFetchingAgentBot: false });
    }
  },

  setAgentBotInbox: async ({ commit }, { inboxId, botId }) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isSettingAgentBot: true });
    try {
      await InboxesAPI.setAgentBot(inboxId, botId);
      commit(types.SET_AGENT_BOT_INBOX, { agentBotId: botId, inboxId });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isSettingAgentBot: false });
    }
  },

  disconnectBot: async ({ commit }, { inboxId }) => {
    commit(types.SET_AGENT_BOT_UI_FLAG, { isDisconnecting: true });
    try {
      await InboxesAPI.setAgentBot(inboxId, null);
      commit(types.SET_AGENT_BOT_INBOX, { agentBotId: '', inboxId });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AGENT_BOT_UI_FLAG, { isDisconnecting: false });
    }
  },

  resetAccessToken: async ({ commit }, botId) => {
    try {
      const response = await AgentBotsAPI.resetAccessToken(botId);
      commit(types.EDIT_AGENT_BOT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  resetSecret: async ({ commit }, botId) => {
    try {
      const response = await AgentBotsAPI.resetSecret(botId);
      commit(types.EDIT_AGENT_BOT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },
};

export const mutations = {
  [types.SET_AGENT_BOT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_AGENT_BOT]: MutationHelpers.setSingleRecord,
  [types.SET_AGENT_BOTS]: MutationHelpers.set,
  [types.EDIT_AGENT_BOT]: MutationHelpers.update,
  [types.DELETE_AGENT_BOT]: MutationHelpers.destroy,
  [types.SET_AGENT_BOT_INBOX]($state, { agentBotId, inboxId }) {
    $state.agentBotInbox = {
      ...$state.agentBotInbox,
      [inboxId]: agentBotId,
    };
  },
  [types.UPDATE_AGENT_BOT_AVATAR]($state, { id, thumbnail }) {
    const botIndex = $state.records.findIndex(bot => bot.id === id);
    if (botIndex !== -1) {
      $state.records[botIndex].thumbnail = thumbnail || '';
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
